/**
 * Remembers, in the reader's own browser, that a local copy answered once.
 *
 * WHY. Probing `127.0.0.1` from a public page can make the browser ask the
 * reader for local network access. Almost no visitor runs a local copy, so
 * the page probes only after the reader asks it to — and, once a probe has
 * succeeded, on every later visit, since that reader has already shown
 * they run one.
 *
 * Every storage call is wrapped: a private window or blocked site data makes
 * storage throw, and the page must work without it — it then just forgets.
 */

const STORAGE_KEY = 'gibs-landing-local-copy'
const REMEMBERED_VALUE = 'found'

/** The two storage calls this module makes — `localStorage` in production. */
export type LocalCopyStorage = Pick<Storage, 'getItem' | 'setItem'>

/**
 * Returns the storage to use. A function, not a value, because merely
 * reading `window.localStorage` can throw when site data is blocked.
 */
export type LocalCopyStorageSource = () => LocalCopyStorage

/** The production storage source. */
export const browserStorage: LocalCopyStorageSource = () => window.localStorage

/** Whether a local copy answered on an earlier visit. `false` when storage is unavailable. */
export const isLocalCopyRemembered = (storageSource: LocalCopyStorageSource): boolean => {
  try {
    return storageSource().getItem(STORAGE_KEY) === REMEMBERED_VALUE
  } catch {
    return false
  }
}

/** Records that a local copy answered. Does nothing when storage is unavailable. */
export const rememberLocalCopy = (storageSource: LocalCopyStorageSource): void => {
  try {
    storageSource().setItem(STORAGE_KEY, REMEMBERED_VALUE)
  } catch {
    // Storage unavailable: the link still shows for this visit.
  }
}
